import React, { useEffect, useState, useContext } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { port } from '../App';
import HomeNavBar from '../Component/HomeComponent/HomeNavBar';
import { Helmet } from 'react-helmet';

import itBanner from '../Assest/banners/Industries/IT & SOFTWARE INDUSTRY.avif';
import healthcareBanner from '../Assest/banners/Industries/HEALTHCARE INDUSTRY.avif';
import manufacturingBanner from '../Assest/banners/Industries/MANUFACTURING INDUSTRY.avif';
import bfsiBanner from '../Assest/banners/Industries/BFSI INDUSTRY.avif';
import ecommerceBanner from '../Assest/banners/Industries/ECOMMERCE INDUSTRY.avif';
import logisticsBanner from '../Assest/banners/Industries/LOGISTICS & SUPPLY CHAIN INDUSTRY.avif';
import startupsBanner from '../Assest/banners/Industries/STARTUPS INDUSTRY.avif';
import gccBanner from '../Assest/banners/Industries/GLOBAL CAPABILITY CENTERS (GCC).avif';

import ITHero from '../Component/Industries/ITHero';
import ITMetrics from '../Component/Industries/ITMetrics';
import ITChallenges from '../Component/Industries/ITChallenges';
import ITRoles from '../Component/Industries/ITRoles';
import ITProcess from '../Component/Industries/ITProcess';
import ITFAQ from '../Component/Industries/ITFAQ';
import ITFinalCTA from '../Component/Industries/FinalCTASection';
import { GlobalEnquiryContext } from '../App';
import { industriesData } from '../Component/IndustriesData';

const IndustryPage = () => {
    const { slug } = useParams();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { openEnquiry } = useContext(GlobalEnquiryContext);

    const pathParts = location.pathname.split('/').filter(Boolean);
    const currentSlug = slug || pathParts[pathParts.length - 1] || 'information-technology';

    useEffect(() => {
        setLoading(true);
        window.scrollTo(0, 0);

        // Check local static fallback registry first
        const localData = industriesData[currentSlug.toLowerCase()];
        if (localData) {
            setData(localData);
            setLoading(false);
            return;
        }

        axios.get(`${port}/api/industry/${currentSlug}/`)
            .then(res => {
                setData(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching industry page data:", err);
                setLoading(false);
            });
    }, [currentSlug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-xl font-semibold text-slate-600 animate-pulse">Loading specialized services...</div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-xl font-semibold text-red-500">Industry details not found.</div>
            </div>
        );
    }

    const triggerEnquiry = () => {
        openEnquiry(`${data.title} Industry Consultation`, data.enquiry_fields);
    };

    const industryImages = {
        'information-technology': itBanner,
        'healthcare': healthcareBanner,
        'manufacturing': manufacturingBanner,
        'bfsi': bfsiBanner,
        'ecommerce': ecommerceBanner,
        'logistics-supply-chain': logisticsBanner,
        'startups': startupsBanner,
        'gccs': gccBanner
    };

    const displayHeroImage = data.hero_image || industryImages[currentSlug] || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80';

    return (
        <div className="bg-slate-50 min-h-screen">
            <Helmet>
                <title>{data.meta_title || `${data.title} Industry Recruitment Services | Merida HR Consulting`}</title>
                <meta name="description" content={data.meta_description || ""} />
                {((data.secondary_keywords && data.secondary_keywords.length > 0) || 
                  (data.long_tail_keywords && data.long_tail_keywords.length > 0)) && (
                    <meta 
                        name="keywords" 
                        content={[
                            ...(data.secondary_keywords || []),
                            ...(data.long_tail_keywords || [])
                        ].join(', ')} 
                    />
                )}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "name": data.title,
                        "provider": {
                            "@type": "LocalBusiness",
                            "name": "Merida HR Consulting",
                            "address": {
                                "@type": "PostalAddress",
                                "streetAddress": "1st Floor, 334/28, 14th Cross Rd, 2nd Block, Jayanagar",
                                "addressLocality": "Bengaluru",
                                "addressRegion": "Karnataka",
                                "postalCode": "560011",
                                "addressCountry": "IN"
                            }
                        },
                        "areaServed": "IN",
                        "description": data.meta_description || data.hero_description
                    })}
                </script>
                {data.faq_list && data.faq_list.length > 0 && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "FAQPage",
                            "mainEntity": data.faq_list.map(f => ({
                                "@type": "Question",
                                "name": f.question,
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": f.answer
                                }
                            }))
                        })}
                    </script>
                )}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": "Home",
                                "item": "https://www.meridahr.com/"
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "name": "Industries",
                                "item": "https://www.meridahr.com/industry"
                            },
                            {
                                "@type": "ListItem",
                                "position": 3,
                                "name": data.title,
                                "item": `https://www.meridahr.com/industries/${currentSlug}`
                            }
                        ]
                    })}
                </script>
            </Helmet>
            
            <HomeNavBar />
            
            <ITHero 
                title={data.hero_title}
                description={data.hero_description}
                primaryCta={data.hero_primary_cta}
                secondaryCta={data.hero_secondary_cta}
                heroImage={displayHeroImage}
                onCtaClick={triggerEnquiry}
            />
            
            <ITMetrics 
                title={data.metrics_title}
                metrics={data.metrics_list}
            />
            
            <ITChallenges 
                complexityTitle={data.complexity_title}
                complexitySubtitle={data.complexity_subtitle}
                complexityPoints={data.complexity_points}
                complexityChallengesIntro={data.complexity_challenges_intro}
                complexityChallenges={data.complexity_challenges}
                complexityConclusion={data.complexity_conclusion}
                challengesTitle={data.challenges_title}
                challengesList={data.challenges_list}
                solutionsTitle={data.solutions_title}
                solutionsIntro={data.solutions_intro}
                solutionsPoints={data.solutions_points}
                solutionsDesignedForTitle={data.solutions_designed_for_title}
                solutionsDesignedForList={data.solutions_designed_for_list}
            />
            
            <ITRoles 
                title={data.roles_title}
                roles={data.roles_list}
            />
            
            <ITProcess 
                title={data.process_title}
                steps={data.process_list}
            />
            
            <ITFAQ 
                title={data.faq_title}
                faqs={data.faq_list}
            />
            
            <ITFinalCTA 
                title={data.final_cta_title}
                description={data.final_cta_description}
                buttonText={data.final_cta_button}
                onCtaClick={triggerEnquiry}
            />
        </div>
    );
};

export default IndustryPage;